import React, { useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
  faNetworkWired,
  faEdit,
  faTrash,
  faToggleOn,
  faToggleOff,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';

/**
 * parseIpToNumber(address)
 *
 * Converts an IPv4 dotted-quad address into a numeric value so addresses
 * can be compared/sorted correctly (e.g. 10.0.0.2 > 10.0.0.10 numeric
 * comparison). Returns 0 for malformed segments.
 */
const parseIpToNumber = (address = '') => {
  return address
    .split('.')
    .map((segment) => Number.parseInt(segment, 10) || 0)
    .reduce((value, segment, index) => value + segment * Math.pow(256, 3 - index), 0);
};

/**
 * getStatusBadge(isActive)
 *
 * Small visual helper that renders a colored status indicator (icon + text)
 * for active/inactive IP addresses.
 */
const getStatusBadge = (isActive, t) => (
  <div className="flex items-center space-x-2">
    <FontAwesomeIcon
      icon={isActive ? faCheck : faTimes}
      className="text-sm"
      style={{ color: isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}
    />
    <span
      className="text-sm font-medium"
      style={{ color: isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}
    >
  {isActive ? t('ipAddresses.table.statuses.active') : t('ipAddresses.table.statuses.inactive')}
    </span>
  </div>
);

const IpTable = ({
  ipAddresses = [],
  loading = false,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onView,
  viewingId = null,
  viewLoading = false,
  searchValue = '',
  statusFilter = 'all',
  onClearFilters,
}) => {
  const { t } = useTranslation();
  /**
   * filteredIps (memoized)
   *
   * Apply the search and status filters to the incoming `ipAddresses` list.
   */
  const filteredIps = useMemo(() => {
    let records = Array.isArray(ipAddresses) ? [...ipAddresses] : [];

    if (false && searchValue) {
      const search = searchValue.toLowerCase();
      records = records.filter((ip) => {
        const addressMatch = ip?.ip_address?.toLowerCase().includes(search);
        const descriptionMatch = ip?.description?.toLowerCase().includes(search);
        return addressMatch || descriptionMatch;
      });
    }

    if (statusFilter !== 'all') {
      const shouldBeActive = statusFilter === 'active';
      records = records.filter((ip) => Boolean(ip?.is_active) === shouldBeActive);
    }

    return records;
  }, [ipAddresses, searchValue, statusFilter]);

  const emptyState = useMemo(() => {
    const isFiltered = Boolean(searchValue) || statusFilter !== 'all';

    return {
      icon: faNetworkWired,
      title: isFiltered
        ? t('ipAddresses.table.emptyState.filteredTitle')
        : t('ipAddresses.table.emptyState.defaultTitle'),
      message: isFiltered
        ? t('ipAddresses.table.emptyState.filteredMessage')
        : t('ipAddresses.table.emptyState.defaultMessage'),
      actionLabel:
        isFiltered && typeof onClearFilters === 'function'
          ? t('ipAddresses.table.emptyState.clearFilters')
          : null,
      onAction: isFiltered ? onClearFilters : null,
    };
  }, [onClearFilters, searchValue, statusFilter, t]);

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.05),
    []
  );

  /**
   * rowProps()
   *
   * Common per-row event handlers used to show a hover background.
   */
  const rowProps = useCallback(
    () => ({
      onMouseEnter: (event) => {
        event.currentTarget.style.backgroundColor = hoverColor;
      },
      onMouseLeave: (event) => {
        event.currentTarget.style.backgroundColor = 'transparent';
      },
    }),
    [hoverColor]
  );

  // Column configuration for DataTable. Each column can specify label,
  // sorting behaviour (sortAccessor or sortCompare) and a renderCell.
  const columns = useMemo(() => [
    {
      id: 'ip_address',
  label: t('ipAddresses.table.columns.ipAddress'),
      sortable: true,
      align: 'center',
      alignSelf: 'center',
      justify: 'center',
      sortCompare: (a, b, direction) => {
        const diff = parseIpToNumber(a?.ip_address) - parseIpToNumber(b?.ip_address);
        return direction === 'asc' ? diff : -diff;
      },
      renderCell: (ip) => (
        <div className="flex mt-2">
          <span className="font-mono font-semibold text-base" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {ip?.ip_address}
          </span>
        </div>
      ),
      skeleton: (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-300 rounded-xl" />
          <div className="w-32 h-4 bg-gray-300 rounded" />
        </div>
      ),
    },
    {
      id: 'description',
  label: t('ipAddresses.table.columns.description'),
      sortable: true,
      align: 'center',
      
      sortAccessor: (ip) => ip?.description || '',
      renderCell: (ip) => (
        <span className="text-base" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          {ip?.description || t('ipAddresses.table.copy.noDescription')}
        </span>
      ),
      skeleton: <div className="w-64 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'is_active',
  label: t('ipAddresses.table.columns.status'),
      sortable: true,
      align: 'center',
      justify: 'center',
      alignItems: 'center',
      sortAccessor: (ip) => (ip?.is_active ? 1 : 0),
      renderCell: (ip) => getStatusBadge(Boolean(ip?.is_active), t),
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'actions',
  label: t('ipAddresses.table.columns.actions'),
      align: 'center',
      sortable: false,
      renderCell: (ip) => {
        const isViewing = viewLoading && viewingId === ip?.id;
        return (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => onView && onView(ip)}
              disabled={isViewing}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-70 disabled:scale-100 disabled:cursor-wait"
              style={{
                              backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1),
                              color: UI_COLORS.NAVIGATION.ACTIVE,
                            }}
              title={t('ipAddresses.table.tooltips.view')}
            >
              <FontAwesomeIcon
                icon={faEye}
                
              />
            </button>

            <button
              onClick={() => onEdit && onEdit(ip)}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                color: UI_COLORS.STATUS.WARNING,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
              }}
              title={t('ipAddresses.table.tooltips.edit')}
            >
              <FontAwesomeIcon icon={faEdit} className="text-sm" />
            </button>

            <button
              onClick={() => {
                if (ip?.is_active) {
                  onDeactivate && onDeactivate(ip);
                } else {
                  onActivate && onActivate(ip);
                }
              }}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                color: ip?.is_active ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.WARNING,
                backgroundColor: colorHelpers.withOpacity(
                  ip?.is_active ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.WARNING,
                  0.1
                ),
              }}
              title={ip?.is_active ? t('ipAddresses.table.tooltips.deactivate') : t('ipAddresses.table.tooltips.activate')}
            >
              <FontAwesomeIcon icon={ip?.is_active ? faToggleOff : faToggleOn} className="text-sm" />
            </button>

            <button
              onClick={() => onDelete && onDelete(ip)}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                color: UI_COLORS.STATUS.ERROR,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
              }}
              title={t('ipAddresses.table.tooltips.delete')}
            >
              <FontAwesomeIcon icon={faTrash} className="text-sm" />
            </button>
          </div>
        );
      },
      skeleton: (
        <div className="flex items-center justify-center space-x-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="w-8 h-8 bg-gray-300 rounded-lg" />
          ))}
        </div>
      ),
    },
  ], [onActivate, onDeactivate, onDelete, onEdit, onView, viewLoading, viewingId, t]);

  return (
    <DataTable
      columns={columns}
      data={filteredIps}
      loading={loading}
      keyField="id"
      enablePagination={false}
      defaultSort={{ field: 'ip_address', direction: 'asc' }}
      getRowProps={rowProps}
      emptyState={emptyState}
    />
  );
};

export default IpTable;